import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import LeftSideBar from '../../../Components/Common/LeftSideBar';
import TopBar from '../../../Components/Common/TopBar';
import PageHeader from '../../../Components/Common/PageHeader';
import functions from '../../../utils/functions';
import RightSideBar from '../../../Components/Common/RightSideBar';
import ActivityLoader from '../../../Components/Common/ActivityLoader';
import TableHeader from '../../../Components/Common/TableHeader';
import CustomizedFilterBar from '../../../Components/Common/CustomizedFilterBar';
import PopCashierDailySummary from '../../../Components/Finance/Cashier/PopCashierDailySummary';
import CashierContext from '../../../Context/CashierContext';
import NoContent from '../../../Components/Common/NoContent';

const SalesPerCashier = () => {
    const {
        salesListing,
        refresh,
        resetData,
        filterData
    } = useContext(CashierContext);

    const [selectedCashierId, setSelectedCashierId] = useState(null);
    const [showCashierSlip, setShowCashierSlip] = useState(false);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

    useEffect(() => {
        const loadSales = async () => {
            try {
                await refresh();
            } finally {
                setHasLoadedOnce(true);
            }
        };

        loadSales();

        // Keep this empty to avoid repeated API calls if refresh is recreated by context.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleViewSlip = useCallback((cashierId) => {
        if (!cashierId) return;

        setSelectedCashierId(cashierId);
        setShowCashierSlip(true);

        setTimeout(() => {
            functions.openPopup('modal_cashier_daily_summary');
        }, 0);
    }, []);

    const handleRefresh = useCallback(async () => {
        await refresh();
    }, [refresh]);

    const buttons = useMemo(() => (
        <>
            <a
                href="#"
                className="btn ripple btn-secondary navresponsive-toggler"
                data-toggle="collapse"
                data-target="#navbarSupportedContent"
                aria-controls="navbarSupportedContent"
                aria-expanded="false"
                aria-label="Toggle navigation"
                onClick={(event) => event.preventDefault()}
            >
                <i className="fe fe-filter mr-1"></i>
                Filter
                <i className="fas fa-caret-down ml-1"></i>
            </a>

            <button
                type="button"
                className="btn ripple btn-light"
                onClick={handleRefresh}
            >
                <i className="fa fa-redo"></i> Refresh
            </button>
        </>
    ), [handleRefresh]);

    const hasRecords = Array.isArray(salesListing) && salesListing.length > 0;
    const isEmpty = salesListing === '404' || (Array.isArray(salesListing) && salesListing.length === 0);
    const isLoading = !hasLoadedOnce && !salesListing;

    const renderCashierRows = () => {
        if (!hasRecords) return null;

        return salesListing.map((item, index) => {
            const userInfo = item.user_info || {};
            const salesInfo = item.sales_info || {};
            const cashBank = salesInfo.cash_bank || {};

            return (
                <tr key={userInfo.user_id || index}>
                    <td>{index + 1}</td>
                    <td>{userInfo.user_role?.role_name || 'N/A'}</td>
                    <td>{`${userInfo.last_name || ''} ${userInfo.first_name || ''}`}</td>
                    <td>{userInfo.phone_number || 'N/A'}</td>
                    <td>{userInfo.username || 'N/A'}</td>
                    <td>{cashBank.cash?.amount_p || '00'}</td>
                    <td>{cashBank.bank?.amount_p || '00'}</td>
                    <td>
                        <button
                            type="button"
                            className="btn ripple btn-primary btn-sm"
                            onClick={() => handleViewSlip(userInfo.user_id)}
                        >
                            View Slip
                        </button>
                    </td>
                </tr>
            );
        });
    };

    return (
        <div className="page">
            <LeftSideBar />

            <div
                className="main-content side-content pt-0"
                onClick={functions.closeSystemDrawer}
            >
                <TopBar />

                <div className="container-fluid">
                    <PageHeader
                        title="Cashier Sales"
                        previousPageTitle="Finance"
                        buttons={buttons}
                        back="/finance"
                    />

                    {showCashierSlip && selectedCashierId && (
                        <PopCashierDailySummary cashierId={selectedCashierId} />
                    )}

                    <CustomizedFilterBar
                        onReset={resetData}
                        onConfirm={filterData}
                    />

                    <div className="row">
                        <div className="col-sm-12 col-md-12 col-xl-12">
                            <div className="card custom-card">
                                <div className="card-body">

                                    <TableHeader
                                        title="List of daily sales per cashier"
                                        subtitle="Sales corresponding to each cashier including bank and cash transactions"
                                    />

                                    <div className="table-responsive">
                                        <table
                                            id="users"
                                            className="table text-nowrap table-hover mg-b-0"
                                        >
                                            <thead>
                                                <tr>
                                                    <th>No.</th>
                                                    <th>Role</th>
                                                    <th>Cashier Name</th>
                                                    <th>Cashier Contact</th>
                                                    <th>Username</th>
                                                    <th>
                                                        Total Cash
                                                        <small className="tx-12 tx-gray-500"> UGX</small>
                                                    </th>
                                                    <th>
                                                        Total Bank
                                                        <small className="tx-12 tx-gray-500"> UGX</small>
                                                    </th>
                                                    <th></th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {renderCashierRows()}
                                            </tbody>
                                        </table>

                                        {!salesListing && <ActivityLoader />}
                                        {salesListing === "404" && <NoContent />}
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            <RightSideBar />
        </div>
    );
};

export default SalesPerCashier;